import {Toast} from 'antd-mobile/es'

import {getCurrentLanguage} from '~/lib/i18n'

import projectConfig from '../../config'
import {notifyAuthExpired} from './authExpired'
import axios from './axios'
import {
  clearStoredDokimoAuth,
  generateSecureNonce,
  getBrowserFingerprint,
  getMobileOS,
  getStoredDokimoAuth,
  isBrowser,
} from './helper'

const getHdt = () => {
  if (!isBrowser()) {
    return 2
  }

  const os = getMobileOS()
  // 安卓0，ios1，其他2
  if (os === 'android') {
    return 0
  }
  if (os === 'ios') {
    return 1
  }
  return 2
}

const getRequestLocale = () => {
  const language = getCurrentLanguage()

  if (language === 'zh-rTW') {
    return 'zh-TW'
  }
  if (language === 'zh-rHK') {
    return 'zh-HK'
  }
  return language
}

interface ReqParams {
  url: string
  params: any
  method?: 'get' | 'post'
  needToken?: boolean // 是否需要token
  useOrigin?: boolean // 是否使用原始域名
  returnAll?: boolean
  onUploadProgress?: (event: ProgressEvent) => void
}

export type ReqError = Error & {
  code?: number
  ret?: number
  errcode?: number
}

function fullURL(url: string) {
  const {proxies = []} = projectConfig[ENV]
  for (let i = 0; i < proxies.length; i += 1) {
    const {match, map, host} = proxies[i]
    if (match.test(url)) {
      const realURL = host + map(url)
      return isBrowser() ? url : realURL
    }
  }
  return url
}

/**
 * 按键名字典序排序对象
 * @param obj 要排序的对象
 * @returns 排序后的新对象
 */
const sortObjectByKeys = (obj: Record<string, any>): Record<string, any> => {
  if (!obj || typeof obj !== 'object') {
    return obj
  }
  const sortedObj: Record<string, any> = {}
  Object.keys(obj)
    .sort() // 按键名字典序排序
    .forEach((key) => {
      sortedObj[key] = obj[key]
    })
  return sortedObj
}

const getCommonData = async () => {
  const deviceId = await getBrowserFingerprint()
  const token = getStoredDokimoAuth()?.token

  return {
    nonce: await generateSecureNonce(),
    deviceId,
    h_did: deviceId,
    h_lang: getCurrentLanguage(),
    h_locale: getRequestLocale(),
    h_app: 'dokimo',
    token,
    h_m: getStoredDokimoAuth()?.memberInfo?.id,
    h_ts: Date.now(),
    h_dt: getHdt(),
    h_market: 'us',
    h_ch: 'web',
    h_av: '1.0.0',
  }
}

const AUTH_EXEMPT_URLS = [
  '/api/account/email_otp_send',
  '/api/account/email_otp_login',
  '/api/account/open_login',
]

const isAuthExpiredCode = (payload: any) => {
  const code = Number(payload?.errcode ?? payload?.code ?? payload?.ret)
  return code === -11
}

const handleAuthExpiredIfNeeded = (url: string, payload: any) => {
  if (!isBrowser() || !isAuthExpiredCode(payload)) {
    return
  }
  if (AUTH_EXEMPT_URLS.some((item) => url.indexOf(item) !== -1)) {
    return
  }
  clearStoredDokimoAuth()
  notifyAuthExpired()
}

const tokenHanlder = () => {
  Toast.clear()
}

const createReqError = (res: any): ReqError => {
  const error = new Error(
    res?.msg || res?.message || 'request failed'
  ) as ReqError
  const code = Number(res?.errcode ?? res?.code ?? res?.ret)
  error.code = Number.isFinite(code) ? code : undefined
  error.ret = Number(res?.ret)
  error.errcode = Number(res?.errcode)
  return error
}

export const getReqErrorCode = (error: unknown): number | undefined => {
  if (error == null || typeof error !== 'object') {
    return undefined
  }

  const err = error as ReqError
  const raw = err.code ?? err.ret ?? err.errcode
  if (raw === undefined || raw === null) {
    return undefined
  }

  const num = Number(raw)
  return Number.isFinite(num) ? num : undefined
}

export const isAuthExpiredError = (error: unknown) =>
  getReqErrorCode(error) === -11

export async function req(args: ReqParams): Promise<any> {
  const {
    url,
    params,
    method = 'post',
    useOrigin = false,
    returnAll = false,
    onUploadProgress,
  } = args
  const commonData = await getCommonData()
  const currentUrl = useOrigin ? url : fullURL(url)
  const currentParams = useOrigin ? params : {...params, ...commonData}
  const requestParams =
    method === 'get'
      ? {params: sortObjectByKeys(currentParams)}
      : {...sortObjectByKeys(currentParams)}

  let res: any
  try {
    res =
      method === 'get'
        ? await axios.get(currentUrl, requestParams)
        : await axios.post(currentUrl, requestParams, {onUploadProgress})
  } catch (error) {
    // 网络异常 / 非 2xx，优先透出后端响应体里的报错信息
    const responseData = (error as any)?.response?.data
    if (responseData && typeof responseData === 'object') {
      handleAuthExpiredIfNeeded(url, responseData)
      throw createReqError(responseData)
    }
    throw error
  }

  if (returnAll) {
    return res
  }
  if (+res.code === 0 || +res.ret === 1 || +res.errcode === 1) {
    return res.data
  }

  handleAuthExpiredIfNeeded(url, res)

  // token 失效（历史错误码），清理 toast 后仍需抛出，避免调用方 Promise 永久挂起
  if ((res.ret === -1006 || res.ret === -1007) && isBrowser()) {
    tokenHanlder()
  }

  throw createReqError(res)
}

export async function reqLog(args: any) {
  const data = await getCommonData()
  const params = {
    ...data,
    list: [args],
    cancelType: 3,
  }
  const url = fullURL('/xs-api/stat/action')
  axios.post(url, {...params})
}
